import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Optional,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import {COMPOSITION_BUFFER_MODE, ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {createTextMaskInputElement} from 'text-mask-core/dist/textMaskCore';

export interface FdTextMaskConfig {
  mask: Array<string | RegExp> | ((rawValue: string) => Array<string | RegExp>) | false;
  guide?: boolean;
  placeholderChar?: string;
  pipe?: (conformedValue: string, config: FdTextMaskConfig) => false | string | object;
  keepCharPositions?: boolean;
  showMask?: boolean;
}

@Directive({
  selector: '[textMask]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextMaskDirective),
      multi: true
    }
  ]
})
export class TextMaskDirective implements ControlValueAccessor, OnChanges {
  @Input('textMask') textMaskConfig: FdTextMaskConfig;

  onChange: (_: any) => void = () => {};
  onTouched: () => void = () => {};

  private textMaskInputElement: {update: (value: string) => void};
  private inputElement: HTMLInputElement;
  private composing = false;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    @Optional() @Inject(COMPOSITION_BUFFER_MODE) private compositionMode: boolean
  ) {
    this.inputElement = this.elementRef.nativeElement;
    if (this.compositionMode == null) {
      this.compositionMode = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.textMaskConfig) {
      this.setupMask();
      if (this.textMaskInputElement) {
        this.textMaskInputElement.update(this.inputElement.value);
      }
    }
  }

  writeValue(value: any): void {
    const normalizedValue = value == null ? '' : value;
    this.renderer.setProperty(this.inputElement, 'value', normalizedValue);
    this.setupMask();
    if (this.textMaskInputElement) {
      this.textMaskInputElement.update(normalizedValue);
    }
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.inputElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event.target.value'])
  handleInput(value: string): void {
    if (this.compositionMode && this.composing) {
      return;
    }

    this.setupMask();

    if (this.textMaskInputElement) {
      this.textMaskInputElement.update(value);
      this.onChange(this.inputElement.value);
      return;
    }

    this.onChange(value);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  @HostListener('compositionstart')
  compositionStart(): void {
    this.composing = true;
  }

  @HostListener('compositionend', ['$event.target.value'])
  compositionEnd(value: string): void {
    this.composing = false;
    this.handleInput(value);
  }

  private setupMask(): void {
    if (!this.textMaskConfig || !this.textMaskConfig.mask) {
      this.textMaskInputElement = null;
      return;
    }

    this.textMaskInputElement = createTextMaskInputElement({
      inputElement: this.inputElement,
      ...this.textMaskConfig
    });
  }
}
